import { MAX_PLAN_DEPTH } from '@timelog/pm-shared';
import type { PlanNode } from '@timelog/pm-shared';

// Imported from the module rather than the editor barrel on purpose: the barrel
// re-exports PlanEditorProvider, which reaches the OIDC session and therefore
// `window`. This module has to stay loadable in the node-environment unit tests.
import { createPlanNode } from '../editor/nodeFactory';

/**
 * Turns an indented text outline into plan nodes — the "Capture tasks" composer.
 *
 * Everything here is pure: parsing, the Tab/Shift+Tab text transform, and the
 * node construction. The dialog only wires them to a textarea.
 */

/** BR-23 caps a plan at 5 levels, so the deepest 0-based depth is 4. */
export const MAX_OUTLINE_DEPTH = MAX_PLAN_DEPTH - 1;

/** Two spaces per level — what Tab inserts and what a pasted outline usually has. */
const SPACES_PER_LEVEL = 2;

export type OutlineLine = {
  depth: number;
  name: string;
  /**
   * The line was typed deeper than it ended up. Either it skipped a level (you
   * cannot jump from depth 0 to depth 2) or it hit the BR-23 ceiling. Surfaced in
   * the preview so the flattening is visible before committing, never silent.
   */
  clamped: boolean;
};

/** Leading `-`, `•`, `*`, `1.` or `1)` from a pasted list. */
const LIST_MARKER = /^(?:[-•*]|\d+[.)])\s*/;

export const parseOutline = (text: string): OutlineLine[] => {
  const lines: OutlineLine[] = [];
  let previousDepth = -1;

  for (const rawLine of text.split(/\r?\n/)) {
    const line = rawLine.replace(/\t/g, ' '.repeat(SPACES_PER_LEVEL));
    if (line.trim() === '') continue;

    const name = line.trim().replace(LIST_MARKER, '').trim();
    // A line that is only a bullet carries no task. Skipping it WITHOUT touching
    // previousDepth matters: otherwise a stray "-" would re-anchor the indent
    // baseline and shift everything below it.
    if (name === '') continue;

    const leadingSpaces = line.length - line.trimStart().length;
    const typedDepth = Math.floor(leadingSpaces / SPACES_PER_LEVEL);
    // previousDepth starts at -1, so the first kept line is always depth 0 —
    // which is why a capture can never produce a root task (BR-2).
    const depth = Math.min(typedDepth, previousDepth + 1, MAX_OUTLINE_DEPTH);

    lines.push({ depth, name, clamped: depth < typedDepth });
    previousDepth = depth;
  }

  return lines;
};

export type OutlineSummary = {
  total: number;
  clampedCount: number;
};

export const outlineSummary = (lines: readonly OutlineLine[]): OutlineSummary => {
  let clampedCount = 0;
  for (const line of lines) {
    if (line.clamped) clampedCount += 1;
  }
  return { total: lines.length, clampedCount };
};

export type ShiftResult = { text: string; selectionStart: number; selectionEnd: number };

/**
 * Tab / Shift+Tab over whichever lines the selection touches, returning the new
 * text and the selection to restore. A caret (no selection) shifts its own line.
 */
export const shiftLines = (
  text: string,
  selectionStart: number,
  selectionEnd: number,
  direction: 'in' | 'out',
): ShiftResult => {
  const firstLineStart = text.lastIndexOf('\n', selectionStart - 1) + 1;
  const lineEndIndex = text.indexOf('\n', selectionEnd);
  const lastLineEnd = lineEndIndex === -1 ? text.length : lineEndIndex;

  const indent = ' '.repeat(SPACES_PER_LEVEL);
  let firstLineDelta = 0;
  let totalDelta = 0;

  const shifted = text
    .slice(firstLineStart, lastLineEnd)
    .split('\n')
    .map((line, index) => {
      const next =
        direction === 'in'
          ? indent + line
          : // Outdent removes up to two leading spaces (or one tab): a line
            // indented by one space loses just that one rather than nothing.
            line.replace(new RegExp(`^(?:\\t| {1,${SPACES_PER_LEVEL}})`), '');
      const delta = next.length - line.length;
      if (index === 0) firstLineDelta = delta;
      totalDelta += delta;
      return next;
    })
    .join('\n');

  return {
    text: text.slice(0, firstLineStart) + shifted + text.slice(lastLineEnd),
    selectionStart: Math.max(firstLineStart, selectionStart + firstLineDelta),
    selectionEnd: Math.max(firstLineStart, selectionEnd + totalDelta),
  };
};

/**
 * Depth 0 becomes an internal `kind: 'deliverable'` node, anything deeper a
 * task (BR-2). The UI has no Deliverable concept — a depth-0 line is simply a
 * top-level task. Iterative: an outline is bounded at 5 levels but the line
 * count is not.
 *
 * Because `parseOutline` already clamps to MAX_OUTLINE_DEPTH, no subtree built
 * here can trip `insertNode`'s depth guard — so nothing a user typed is dropped
 * on commit.
 */
export const outlineToNodes = (
  lines: readonly OutlineLine[],
  makeId?: () => string,
): PlanNode[] => {
  const roots: PlanNode[] = [];
  // openAtDepth[d] is the node most recently opened at depth d; a line at depth
  // d attaches to openAtDepth[d - 1].
  const openAtDepth: PlanNode[] = [];

  for (const line of lines) {
    const node = createPlanNode(line.depth === 0 ? 'deliverable' : 'task', line.name, makeId);
    if (line.depth === 0) {
      roots.push(node);
    } else {
      const parent = openAtDepth[line.depth - 1];
      // parseOutline's `previousDepth + 1` clamp guarantees a parent exists.
      if (!parent) continue;
      parent.children.push(node);
    }
    openAtDepth[line.depth] = node;
    openAtDepth.length = line.depth + 1;
  }

  return roots;
};
